import {
  FaMapMarkerAlt,
  FaRegClock,
  FaStar,
  FaPlane,
  FaPaw,
  FaCamera,
  FaBinoculars,
  FaUtensils,
  FaBed
} from "react-icons/fa";
import { IoMdArrowDropright, IoMdCheckmarkCircle } from "react-icons/io";
import { AppContext } from "../../App";
import { useContext } from "react";

import imageHero from "../../assets/serengeti.jpg";
import imageDay1 from "../../assets/camp.jpg";
import imageDay2 from "../../assets/wild_plains.jpg";
import imageDay3 from "../../assets/lions.jpg";

const hero = {
  kicker: "3 Days Serengeti Wilderness Safari",
  title: "Serengeti Wilderness Adventure",
  subtitle: "Quality mid-range lodge or tented camp in the heart of Serengeti National Park.",
  backgroundImage: imageHero,
  meta: ["3 Days", "Big Five", "Great Migration", "Game Drives"],
};

const overview = `3 Days Serengeti Wilderness Safari includes a stay at a quality mid-range safari lodge or tented camp, typically located inside or close to the main Serengeti National Park. The park is home to the Great Migration and the Big Five, including the lion, leopard, rhino, elephant, and buffalo, roaming the endless plains, with stunning hills, rivers, and acacia trees. During the safari, you will enjoy thrilling game drives in the Serengeti, witness the Great Wildebeest Migration in season, marvel at the endless plains of the Serengeti, and enjoy fantastic birdwatching. You will have great photographic opportunities.`;

const highlights = [
  {
    icon: FaPaw,
    title: "Big Five & Migration",
    description: "Home to Great Migration and Big Five roaming endless plains with stunning landscapes.",
    color: "#f59e0b"
  },
  {
    icon: FaBinoculars,
    title: "Game Drives & Activities",
    description: "Thrilling game drives, bush walks, hot air balloon safaris, birdwatching.",
    color: "#059669"
  },
  {
    icon: FaCamera,
    title: "Photography",
    description: "Great photographic opportunities with predators and migration spectacles.",
    color: "#7c3aed"
  },
  {
    icon: FaBed,
    title: "Prime Lodging",
    description: "Mid-range lodge or tented camp inside Serengeti National Park.",
    color: "#d97706"
  },
];

const details = [
  {
    icon: FaMapMarkerAlt,
    title: "Location",
    description: "Serengeti National Park, Tanzania.",
  },
  {
    icon: FaRegClock,
    title: "Duration",
    description: "3 days of immersive wildlife adventure.",
  },
  {
    icon: FaUtensils,
    title: "Meals",
    description: "Full board on safari days. Breakfast on departure day.",
  },
  {
    icon: FaBed,
    title: "Accommodation",
    description: "Exclusive wilderness camp or mid-range safari lodge.",
  },
];

const itinerary = [
  {
    dayLabel: "Day 1",
    dayNum: "01",
    title: "Arrival in Arusha and Transfer to Serengeti",
    image: imageDay1,
    body: "Arrive in Arusha and transfer to Serengeti National Park by flight or road. Upon arrival, embark on a thrilling private game drive en route to your exclusive wilderness camp. Settle into your comfortable tent and experience the untamed beauty of the Serengeti. Accommodation: Exclusive wilderness camp in Serengeti National Park.",
    activities: "Transfer, game drive, camp check-in",
    tags: ["Arrival", "Game Drive", "Wilderness Camp"]
  },
  {
    dayLabel: "Day 2",
    dayNum: "02",
    title: "Full Day Adventure",
    image: imageDay2,
    body: "Engage in full-day game drive, which may include guided bush walks, hot air balloon safaris, and immersive experiences with the local flora and fauna. Explore the hidden gems of the Serengeti and witness its diverse wildlife. Return to your exclusive wilderness camp for an evening of relaxation around the campfire. Accommodation: Exclusive wilderness camp in Serengeti National Park.",
    activities: "Meal Plan: Breakfast, Lunch, Dinner",
    tags: ["Full Day Safari", "Bush Walk", "Balloon Option"]
  },
  {
    dayLabel: "Day 3",
    dayNum: "03",
    title: "Morning Safari and Departure",
    image: imageDay3,
    body: "Embark on an early morning safari, capturing the serene beauty of the Serengeti at sunrise. Afterward, enjoy a final breakfast in the wilderness before departing for Arusha by flight or road, concluding your unforgettable Serengeti Wilderness Safari. Accommodation: None (day of departure).",
    activities: "Meal Plan: Breakfast",
    tags: ["Sunrise Safari", "Departure", "Final Memories"]
  },
];

export default function SerengetiWilderness() {
  const appContext = useContext(AppContext);
  
  // Safari theme: Earth tones, amber, warm neutrals
  const theme = {
    primary: "#d97706",      // Amber-600
    primaryLight: "#f59e0b", // Amber-500
    accent: "#92400e",       // Amber-800
    bg: "#fffbeb",           // Amber-50
    cardBg: "#ffffff",
    text: "#1e293b",
    textMuted: "#64748b"
  };

  return (
    <div style={{ 
      maxWidth: "1400px", 
      margin: "0 auto", 
      fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      color: theme.text,
      background: theme.bg
    }}>
      
      {/* ================= HERO SECTION ================= */}
      <section
        style={{
          height: "75vh",
          minHeight: "500px",
          backgroundImage: `linear-gradient(135deg, rgba(23, 15, 8, 0.9), rgba(120, 53, 15, 0.75)), url(${imageHero})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          textAlign: "center",
          padding: "0 1.5rem",
          position: "relative"
        }}
      >
        <div style={{ maxWidth: "800px", animation: "fadeInUp 0.8s ease-out" }}>
          <span 
            className="badge rounded-pill px-4 py-2 mb-4"
            style={{ 
              background: "rgba(245,158,11,0.2)", 
              border: "1px solid rgba(245,158,11,0.4)",
              color: "#fcd34d",
              fontSize: "0.9rem",
              fontWeight: "600",
              letterSpacing: "0.5px"
            }}
          >
            {hero.kicker}
          </span>
          
          <h1 
            style={{ 
              fontSize: "clamp(2.5rem, 6vw, 4rem)", 
              marginBottom: "1rem", 
              lineHeight: 1.1,
              fontWeight: "700",
              textShadow: "0 2px 10px rgba(0,0,0,0.4)"
            }}
          >
            {hero.title}
          </h1>
          
          <p 
            style={{ 
              fontSize: "clamp(1.1rem, 2.5vw, 1.35rem)", 
              marginBottom: "2.5rem",
              opacity: 0.95,
              lineHeight: 1.6
            }}
          >
            {hero.subtitle}
          </p>
          
          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
            {hero.meta.map((m, i) => (
              <span 
                key={i} 
                style={{ 
                  background: "rgba(255,255,255,0.15)", 
                  padding: "0.5rem 1.25rem", 
                  borderRadius: "50px",
                  fontSize: "0.9rem",
                  fontWeight: "500",
                  backdropFilter: "blur(4px)"
                }}
              >
                {m}
              </span>
            ))}
          </div>
        </div>
      </section>

      <div style={{ padding: "4rem 1.5rem" }}>
        
        {/* ================= OVERVIEW ================= */}
        <section style={{ marginBottom: "5rem", textAlign: "center", maxWidth: "800px", margin: "0 auto 5rem" }}>
          <h2 
            style={{ 
              fontSize: "clamp(1.75rem, 4vw, 2.25rem)", 
              marginBottom: "1.5rem", 
              color: theme.text,
              fontWeight: "700"
            }}
          >
            Experience the Serengeti
          </h2>
          <p 
            style={{ 
              fontSize: "1.1rem", 
              lineHeight: 1.8, 
              color: theme.textMuted,
              marginBottom: "2rem"
            }}
          >
            {overview}
          </p>
          
          {/* Quick Stats */}
          <div className="row g-3 justify-content-center" style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            {details.map((d, i) => {
              const DIcon = d.icon;
              return (
                <div 
                  key={i}
                  className="col-6 col-md-3"
                  style={{
                    background: theme.cardBg,
                    padding: "1rem 1.5rem",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    minWidth: "200px",
                    border: "1px solid rgba(0,0,0,0.04)"
                  }}
                >
                  <DIcon size={22} style={{ color: theme.primary, flexShrink: 0 }} />
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: "0.8rem", fontWeight: "600", color: theme.text }}>{d.title}</div>
                    <div style={{ fontSize: "0.85rem", color: theme.textMuted }}>{d.description}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ================= HIGHLIGHTS GRID ================= */}
        <section style={{ marginBottom: "5rem" }}>
          <div className="text-center mb-4">
            <h2 
              style={{ 
                fontSize: "clamp(1.75rem, 4vw, 2.25rem)", 
                marginBottom: "0.5rem", 
                color: theme.text,
                fontWeight: "700"
              }}
            >
              Safari Highlights
            </h2>
            <p style={{ color: theme.textMuted }}>Unforgettable wildlife encounters await</p>
          </div>
          
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", 
            gap: "1.5rem" 
          }}>
            {highlights.map((h, i) => {
              const HIcon = h.icon;
              return (
                <article 
                  key={i} 
                  style={{ 
                    background: theme.cardBg,
                    padding: "1.75rem",
                    borderRadius: "16px",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    cursor: "default",
                    border: "1px solid rgba(0,0,0,0.04)"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "0 12px 28px rgba(0,0,0,0.12)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.08)";
                  }}
                >
                  <div 
                    className="d-inline-flex align-items-center justify-content-center rounded-2 mb-3"
                    style={{
                      width: "52px",
                      height: "52px",
                      background: `${h.color}15`,
                      color: h.color,
                      fontSize: "1.4rem"
                    }}
                  >
                    <HIcon />
                  </div>
                  <h3 style={{ fontSize: "1.25rem", marginBottom: "0.75rem", fontWeight: "600", color: theme.text }}>
                    {h.title}
                  </h3>
                  <p style={{ color: theme.textMuted, lineHeight: 1.6, fontSize: "0.95rem", margin: 0 }}>
                    {h.description}
                  </p>
                </article>
              );
            })}
          </div>
        </section>

        {/* ================= ITINERARY TIMELINE ================= */}
        <section>
          <div className="text-center mb-5">
            <h2 
              style={{ 
                fontSize: "clamp(1.75rem, 4vw, 2.25rem)", 
                marginBottom: "0.5rem", 
                color: theme.text,
                fontWeight: "700"
              }}
            >
              Your 3-Day Safari Journey
            </h2>
            <p style={{ color: theme.textMuted }}>Day-by-day adventure through the Serengeti</p>
          </div>

          <div style={{ position: "relative" }}>
            {/* Timeline connector line (desktop) */}
            <div 
              className="d-none d-lg-block"
              style={{
                position: "absolute",
                left: "50%",
                top: "0",
                bottom: "0",
                width: "2px",
                background: `linear-gradient(to bottom, ${theme.primary}33, ${theme.accent}33, transparent)`,
                transform: "translateX(-50%)"
              }}
            />

            {itinerary.map((day, i) => (
              <article 
                key={i} 
                className={`row g-4 align-items-center mb-4 ${i % 2 === 0 ? 'flex-lg-row' : 'flex-lg-row-reverse'}`}
                style={{
                  background: theme.cardBg,
                  borderRadius: "20px",
                  overflow: "hidden",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                  border: "1px solid rgba(0,0,0,0.04)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  position: "relative",
                  zIndex: 1
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.boxShadow = "0 14px 35px rgba(0,0,0,0.12)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.08)";
                }}
              >
                {/* Day Number Badge */}
                <div 
                  className="position-absolute top-0 start-0 m-3 rounded-pill px-3 py-1"
                  style={{
                    background: theme.primary,
                    color: "white",
                    fontWeight: "700",
                    fontSize: "0.85rem",
                    zIndex: 2,
                    boxShadow: "0 4px 10px rgba(217, 119, 6, 0.3)"
                  }}
                >
                  Day {day.dayNum}
                </div>

                {/* Image Column */}
                <div className="col-lg-5" style={{ position: "relative", minHeight: "280px" }}>
                  <div
                    style={{
                      height: "100%",
                      minHeight: "280px",
                      backgroundImage: `url(${day.image})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center"
                    }}
                  />
                  {/* Gradient overlay for text readability on image */}
                  <div 
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "linear-gradient(to top, rgba(23,15,8,0.7), transparent 60%)"
                    }}
                  />
                </div>

                {/* Content Column */}
                <div className="col-lg-7 p-4 p-lg-5">
                  <h3 
                    style={{ 
                      fontSize: "1.4rem", 
                      marginBottom: "1rem", 
                      fontWeight: "700",
                      color: theme.text,
                      paddingRight: "60px"
                    }}
                  >
                    {day.title}
                  </h3>
                  
                  <p style={{ lineHeight: 1.7, color: theme.textMuted, marginBottom: "1.25rem", fontSize: "0.95rem" }}>
                    {day.body}
                  </p>
                  
                  {/* Tags */}
                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1.25rem" }}>
                    {day.tags.map((tag, idx) => (
                      <span 
                        key={idx}
                        style={{
                          background: `${theme.primary}10`,
                          color: theme.primary,
                          padding: "0.35rem 0.85rem",
                          borderRadius: "50px",
                          fontSize: "0.8rem",
                          fontWeight: "500"
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  {/* Meals Info */}
                  <div 
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.6rem 1rem",
                      background: `${theme.accent}10`,
                      borderRadius: "8px",
                      fontSize: "0.9rem",
                      color: theme.accent,
                      fontWeight: "500"
                    }}
                  >
                    <FaUtensils size={14} />
                    {day.activities}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* ================= CTA SECTION ================= */}
        <section 
          className="text-center mt-6 pt-5"
          style={{ 
            borderTop: `1px solid rgba(0,0,0,0.06)`,
            background: `linear-gradient(135deg, ${theme.primary}08, ${theme.accent}08)`,
            borderRadius: "20px",
            padding: "3rem 2rem",
            marginTop: "4rem"
          }}
        >
          <FaPaw size={42} style={{ color: theme.primary, marginBottom: "1rem" }} />
          <h3 
            style={{ 
              fontSize: "clamp(1.5rem, 4vw, 2rem)", 
              fontWeight: "700", 
              marginBottom: "1rem",
              color: theme.text
            }}
          >
            Ready for Your Serengeti Adventure?
          </h3>
          <p style={{ color: theme.textMuted, maxWidth: "600px", margin: "0 auto 2rem", lineHeight: 1.6 }}>
            Book your 3-day wilderness safari and witness the Great Migration and Big Five in their natural habitat. Limited camp spots available.
          </p>
          <button 
            className="btn btn-lg px-5 py-3 rounded-pill"
            style={{ 
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryLight})`,
              border: "none",
              fontWeight: "600",
              color: "white",
              boxShadow: `0 6px 20px ${theme.primary}40`,
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = `0 8px 25px ${theme.primary}50`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = `0 6px 20px ${theme.primary}40`;
            }}
            onClick={() => appContext.navTo({ item: 'plan-trip' })}
          >
            Book This Safari <IoMdArrowDropright size={20} />
          </button>
        </section>

      </div>

      {/* ================= CSS ANIMATIONS ================= */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          * { transition: none !important; animation: none !important; }
        }
        @media (max-width: 991px) {
          [style*="left: 50%"] { display: none !important; }
        }
      `}</style>
    </div>
  );
}